import { useCreateReportGroup } from '@/hooks/api/reports/useCreateReportGroup';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const useStyles = createUseStyles(theme => ({
  CreateReportGroup_dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),

    '&:first-of-type': {
      paddingTop: theme.spacing(2),
    },
  },
  CreateReportGroup_instructions: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

export const CreateReportGroupModal = ({
  isOpen,
  onClose,
  reportId,
}: {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
}) => {
  const c = useStyles();
  const navigate = useNavigate();
  const { getPath } = useTenants();

  const [groupName, setGroupName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { mutate, isPending } = useCreateReportGroup({
    reportId,
    onSuccess: group => {
      setGroupName('');
      setErrorMessage(undefined);
      onClose();
      navigate(getPath(`report/${reportId}/${group.id}`));
    },
    onError: error => {
      setErrorMessage(error);
    },
  });

  const handleSubmit = () => {
    setErrorMessage(undefined);
    mutate({ name: groupName.trim() || undefined });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGroupName(event.target.value);
    setErrorMessage(undefined);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isPending) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleClose = () => {
    if (!isPending) {
      setGroupName('');
      setErrorMessage(undefined);
      onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            width: '405px',
          },
        },
      }}
    >
      <DialogTitle>Create New Report Group</DialogTitle>
      <DialogContent className={c.CreateReportGroup_dialogContent}>
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
        <TextField
          label='Group Name (optional)'
          value={groupName}
          onChange={handleNameChange}
          onKeyDown={handleKeyDown}
          fullWidth
          error={!!errorMessage}
          autoFocus
          placeholder='Duplicate Parts'
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} disabled={isPending} sx={{ width: '100px' }}>
          {isPending ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
